/* eslint-disable max-lines */
import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  signal,
  ViewChild,
  ViewContainerRef,
  inject,
  input,
} from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import type { UserBase } from '@core/account-types';
import { FullCandidateSelectors } from '@core/candidate-data';
import { TimeService } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { InterviewResultRequestActions } from '@core/interview-result-request-data';
import type {
  InterviewResultRequest,
  InterviewResultRequestCandidatePlacement,
} from '@core/interview-result-request-types';
import { AppNavigatorService } from '@core/navigation-legacy';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import { DateComponent } from '@core/time-ui';
import type { DialogRef } from '@core/user-interface-legacy';
import {
  AvatarComponent,
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  ConnectedOverlayDirective,
  DialogService,
  DialogWidth,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  IconColorDirective,
  IconComponent,
  NotificationsController,
  OverlayOriginDirective,
  RtStatefulComponent,
  RtTooltip,
  ScrollableDirective,
  TruncateComponent,
} from '@core/user-interface-legacy';

import { mapPlacements } from './map-placement';

type State = {
  meetingDate: string | null;
  loading: string | boolean;
};

@Component({
  selector: 'rt-interview-result-request-card',
  templateUrl: 'interview-result-request-card.component.html',
  styleUrls: ['./interview-result-request-card.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BoxComponent,
    IconColorDirective,
    IconComponent,
    AvatarComponent,
    BadgeComponent,
    HeadingComponent,
    RtTooltip,
    OfferStatusIndicatorComponent,
    DateComponent,
    GroupComponent,
    NgTemplateOutlet,
    IconButtonComponent,
    ButtonLoadingDirective,
    TruncateComponent,
    ButtonComponent,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownHeaderComponent,
    ScrollableDirective,
    DropdownItemComponent,
    UpperCasePipe,
    TranslatePipe,
  ],
})
export class InterviewResultRequestCardComponent extends RtStatefulComponent<State> {
  private readonly dialogService = inject(DialogService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly i18n = inject(I18nService);
  private readonly notificationsCtrl = inject(NotificationsController);
  private readonly appNavigator = inject(AppNavigatorService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly timeService = inject(TimeService);
  protected readonly cdRef: ChangeDetectorRef;

  public readonly candidateId = input<number>(undefined!);
  public readonly short = input<boolean>(false);
  public readonly dialogRef = input<DialogRef>(undefined!);
  public readonly inDashboard = input<boolean>(false);
  public readonly inCandidate = input<boolean>(false);
  public readonly openCandidateOnClick = input<boolean>(false);
  public readonly openEventModalOnClick = input<boolean>(false);
  public readonly currentAdmin = input<UserBase>(undefined!);
  public readonly hasHighLevelDataAccess = input<boolean>(false);
  @Input() public request: InterviewResultRequest;

  @ViewChild('selectActionDropdown')
  public selectActionDropdown: ConnectedOverlayDirective;
  @ViewChild('dropdownOrigin')
  public dropdownOrigin: OverlayOriginDirective;

  public dropdownConfig: ConnectionPositionPair[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ];

  protected candidatePlacements = signal(<InterviewResultRequestCandidatePlacement[]>[]);
  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      meetingDate: null,
      loading: false,
    });
  }

  public get dueDateLabel(): string | null {
    return this.request.dueDate
      ? this.timeService.format({ input: this.request.dueDate, pattern: 'DD MMM YYYY' })
      : null;
  }

  public get daysOverdue(): number | null {
    if (this.request.status !== 'pending' || !this.request.dueDate) {
      return null;
    }

    const days = this.timeService.daysFromToday(this.request.dueDate);

    return days > 0 ? days : null;
  }

  public ngOnInit(): void {
    if (!this.short() && this.request.interviewEvent) {
      const date = moment(this.request.interviewEvent.startsAt);
      const duration = this.request.interviewEvent.duration;

      date.tz(this.request.interviewEvent.timezone);

      this.setState({
        ...this.state,
        meetingDate: `${date.format('DD MMM YYYY')}, ${date.format('LT')} - ${date
          .add(duration, 'm')
          .format('LT')}`,
      });
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public isOwn(admin: { id: number }): boolean {
    return admin && this.currentAdmin()?.id === admin.id;
  }

  public isCreatedByAdmin(): boolean {
    return this.request.requesterId === this.currentAdmin().id;
  }

  public dismiss(event: MouseEvent, id: number): void {
    event.stopPropagation();
    this.setState({
      ...this.state,
      loading: 'dismiss',
    });

    this.store.dispatch(InterviewResultRequestActions.dismiss({ payload: { id } }));
    this.actions$
      .pipe(
        ofType(InterviewResultRequestActions.dismissSuccess),
        filter(({ payload }) => payload.data.id === id),
        take(1),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => {
        const message = this.i18n.translate('app.interview.pending_dismiss_notification');
        this.notificationsCtrl.success(message);
        this.setState({
          ...this.state,
          loading: false,
        });
      });
  }

  public revert(event: MouseEvent, id: number): void {
    event.stopPropagation();
    this.setState({
      ...this.state,
      loading: 'revert',
    });
    this.store.dispatch(InterviewResultRequestActions.revert({ payload: { id } }));
    this.actions$
      .pipe(
        ofType(InterviewResultRequestActions.revertSuccess),
        filter(({ payload }) => payload.data.id === id),
        take(1),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => {
        const message = this.i18n.translate('app.interview.pending_revert_notification');
        this.notificationsCtrl.success(message);
        this.setState({
          ...this.state,
          loading: false,
        });
      });
  }

  public delete(event: MouseEvent, id: number): void {
    event.stopPropagation();
    const options = {
      title: this.i18n.translate('app.common.confirm_delete'),
      message: this.i18n.translate('app.interview.pending_delete_confirmation'),
      variant: 'error',
      isCancelable: true,
    };

    this.dialogService.confirm(options, this.viewContainerRef).subscribe(confirmed => {
      if (confirmed) {
        this.setState({
          ...this.state,
          loading: 'delete',
        });
        this.store.dispatch(
          InterviewResultRequestActions.removeOnePessimistic({ payload: { data: id } }),
        );
        this.actions$
          .pipe(
            ofType(InterviewResultRequestActions.removeOnePessimisticSuccess),
            filter(({ payload }) => payload.data.id === id),
            take(1),
            takeUntil(this.ngOnDestroy$),
          )
          .subscribe(() => {
            const message = this.i18n.translate('app.interview.pending_delete_notification');
            this.notificationsCtrl.success(message);
            this.setState({
              ...this.state,
              loading: false,
            });
          });
      }
    });
  }

  public startInterview(event: MouseEvent): void {
    event.stopPropagation();

    const hasPlacementsProvidedInRequest = this.hasPlacementsProvidedInRequest(this.request);

    if (hasPlacementsProvidedInRequest) {
      this.candidatePlacements.set(this.request.candidatePlacements);
      this.startInterviewWithPlacements(this.request.candidatePlacements);
      return;
    }

    this.store
      .select(
        FullCandidateSelectors.currentUserAvailablePlacements({
          candidateId: this.request.candidateId,
        }),
      )
      .pipe(take(1), takeUntil(this.ngOnDestroy$))
      .subscribe(placements => {
        const mappedPlacements = mapPlacements(placements);
        this.candidatePlacements.set(mappedPlacements);
        this.startInterviewWithPlacements(mappedPlacements);
      });
  }

  public isStatus(status: string): boolean {
    return this.request.status === status;
  }

  public openCandidate(request: InterviewResultRequest): void {
    this.appNavigator.openCandidateDialog(request.candidateId, {
      tab: 'interviews',
    });
  }

  public openEventModal(event: MouseEvent, request: InterviewResultRequest): void {
    event.stopPropagation();

    this.appNavigator.openInterviewEventDialog(request.interviewEventId!, {
      config: { width: DialogWidth.SmallX },
    });
  }

  public openCandidateDialogWithInterview(offerId?: number, talentPoolId?: number): void {
    this.appNavigator.openCandidateDialog(this.candidateId() || this.request.candidateId, {
      openInterview: true,
      interviewOfferId: offerId,
      interviewTalentPoolId: talentPoolId,
      interviewTemplateId: this.request.interviewTemplateId,
      interviewResultRequestId: this.request.id,
    });

    const dialogRef = this.dialogRef();
    if (dialogRef) {
      dialogRef.close();
    }
  }

  private hasPlacementsProvidedInRequest(request: InterviewResultRequest): boolean {
    return Array.isArray(request.candidatePlacements);
  }

  private startInterviewWithPlacements(
    placements: InterviewResultRequestCandidatePlacement[],
  ): void {
    if (
      this.isCandidateAssignedToOffer(this.request.offerId || this.request.talentPoolId, placements)
    ) {
      this.openCandidateDialogWithInterview(
        this.request.offerId ?? undefined,
        this.request.talentPoolId ?? undefined,
      );
    } else if (placements?.length) {
      this.selectActionDropdown.openAt(this.dropdownOrigin);
    } else {
      this.openCandidateDialogWithInterview();
    }
  }

  private isCandidateAssignedToOffer(
    offerId: number | null,
    placements: InterviewResultRequestCandidatePlacement[],
  ): boolean {
    if (!placements?.length) {
      return false;
    }

    return placements.some(
      candidatePlacement =>
        candidatePlacement.offerId === offerId || candidatePlacement.talentPoolId === offerId,
    );
  }
}
